import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// @ts-ignore
import { QuestionMarkOutlined } from '@netbasequid/core'
// @ts-ignore
import { SimpleBar, Sparkline } from '@netbasequid/chart'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { InsightData } from 'schemas/action/InsightDataSchema'
import TableChart, { ColDef, ColDefs } from 'components/visualization/Table'
import { getTwEmoji } from 'utils/emojiUtil'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { useEmojiNameToDisplayMap } from 'hooks/useEmojiState'
import messageUtil from 'utils/messageUtil'
import {
  getNumberFormatter,
  getMaxValue,
  getSortingConfigByTableSortOption,
  convertToTableData,
  getDisplayText,
  getDisplayTrendScore,
  handleProgressDisplay
} from './utils'

import { useDisplayTheme } from '../../../../../hooks/useUiState'
import { InsightCount } from '../../../../../schemas/action/InsightCountSchema'
import { TableSortOption } from '../../../../../enums'
import { Theme } from '../../../../../features/ui/types'
import { StyledIconContainer, StyledLinkIcon, StyledContent } from './index.css'
import { TrendingWidgetsType, TrendingInsight } from '../../types'
import openHelpPage from '../../../../../utils/helpLinkUtil'

interface TableProps {
  widgetId: number
  onRowClick?: (row: InsightData) => void
}

const Table = ({ widgetId, onRowClick }: TableProps) => {
  const theme = useDisplayTheme() as Theme
  const { t } = useTranslation()
  const widgetSetting = useDashboardWidgetSetting<TrendingWidgetsType>(widgetId)
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const emojiNameToDisplayMap = useEmojiNameToDisplayMap()

  const rowData = useMemo(() => convertToTableData(widgetData?.data, widgetSetting), [
    widgetData,
    widgetSetting
  ]) as TrendingInsight[]

  const timeUnit = rowData?.[0]?.aggregateResult?.timeUnit
  const maxValue = getMaxValue(rowData)
  const { postVol: isShowPostCols, viewOption, tableSortOption } = widgetSetting

  const colDefs: ColDefs<TrendingInsight> = [
    {
      field: 'name',
      headerText: messageUtil.viewOption(viewOption!),
      cellProps: {
        width: isShowPostCols ? '25%' : '50%',
        textAlign: 'left'
      },
      comparator: (rowA: InsightData, rowB: InsightData) =>
        getDisplayText(rowA.name!, viewOption!, emojiNameToDisplayMap) <
        getDisplayText(rowB.name!, viewOption!, emojiNameToDisplayMap)
          ? -1
          : 1,
      customCellRender: (cellValue) => (
        <StyledContent>
          {getTwEmoji(getDisplayText(cellValue!, viewOption!, emojiNameToDisplayMap)) ||
            getDisplayText(cellValue!, viewOption!, emojiNameToDisplayMap)}
        </StyledContent>
      ),
      defaultOrder: TableSortOption.ASCEND
    },
    {
      field: 'trend',
      headerText: t('trendScore'),
      headerProps: {
        textAlign: 'left',
        icon: {
          render: (
            <StyledLinkIcon onClick={() => openHelpPage(8239818)}>
              <QuestionMarkOutlined fontSize={14} color={sys[theme].text.placeholder} />
            </StyledLinkIcon>
          ),
          tooltipContent: (
            <>
              The Trend Score value indicates whether an insight is trending up or down. Trends with
              a score between 0 and 1 are trending down; trends with a score between 1 and 100 are
              trending up. <br />
              Click the help icon for more information.
            </>
          )
        }
      },
      cellProps: {
        width: isShowPostCols ? '25%' : '50%',
        textAlign: 'left'
      },
      comparator: (rowA: InsightData, rowB: InsightData) => (rowA.trend! > rowB.trend! ? 1 : -1),
      children: [
        {
          customCellRender: (cellValue) => <>{getDisplayTrendScore(cellValue)}</>,
          cellProps: {
            colSidePaddings: { right: '8px' },
            textAlign: 'right',
            fitWidth: true
          }
        },
        {
          customCellRender: (cellValue) => (
            <SimpleBar
              progress={handleProgressDisplay(cellValue)}
              colorSetting="metric.mainMetrics.trendScore"
              mode={theme}
            />
          ),
          cellProps: {
            colSidePaddings: { left: '0px' },
            textAlign: 'left'
          }
        }
      ],
      defaultOrder: TableSortOption.DESCEND
    }
  ]

  if (isShowPostCols) {
    colDefs.push(
      {
        field: 'lastPeriodValue',
        headerText: `${t('post_other')} (${
          messageUtil.trendingLastPeriodString(timeUnit) || t('lastPeriod')
        })`,
        headerProps: {
          textAlign: 'left'
        },
        cellProps: {
          width: '25%',
          textAlign: 'left'
        },
        comparator: (rowA, rowB) => (rowA.lastPeriodValue! > rowB.lastPeriodValue! ? 1 : -1),
        children: [
          {
            customCellRender: (cellValue) => <>{getNumberFormatter().format(cellValue!)}</>,
            cellProps: {
              colSidePaddings: { right: '8px' },
              textAlign: 'right',
              fitWidth: true
            }
          },
          {
            customCellRender: (cellValue) => (
              <SimpleBar
                progress={handleProgressDisplay((cellValue! / maxValue) * 100)}
                colorSetting="metric.mainMetrics.post"
                mode={theme}
              />
            ),
            cellProps: {
              colSidePaddings: { left: '0px' },
              textAlign: 'left'
            }
          }
        ],
        defaultOrder: TableSortOption.DESCEND
      } as ColDef<TrendingInsight, any, 'lastPeriodValue'>,
      {
        field: 'aggregateResult',
        headerText: `${t('post_other')} ${
          messageUtil.trendingHistoryString(timeUnit) || t('lastPeriod')
        } ${t('history')}`,
        headerProps: {
          textAlign: 'left',
          icon: {
            render: (
              <StyledIconContainer>
                <QuestionMarkOutlined fontSize={14} color={sys[theme].text.placeholder} />
              </StyledIconContainer>
            ),
            tooltipContent:
              'A sparkline that indicates where volume change occurred across the current analysis period.'
          }
        },
        cellProps: {
          textAlign: 'left',
          width: '25%'
        },
        customCellRender: (cellValue) => (
          <div
            style={{
              width: '100%',
              minWidth: '100px',
              height: '32px',
              maxWidth: `${
                cellValue!.values!.length! > 1 ? (cellValue!.values!.length! - 1) * 15 : 0
              }px`
            }}>
            <Sparkline
              data={cellValue!
                .values!.filter((val) => val !== undefined && val !== null)
                .map((val) => parseInt(val!, 10))}
              mode={theme}
            />
          </div>
        )
      } as ColDef<TrendingInsight, any, 'aggregateResult'>
    )
  }

  return (
    <TableChart
      configs={{
        colSortingConfigs: [getSortingConfigByTableSortOption(tableSortOption)],
        keyField: 'name'
      }}
      rowData={rowData}
      colDefs={colDefs}
      isDisplayFoot={false}
      onRowClick={(tableItem) => {
        const insightData = rowData.find((item) => item.name === tableItem.name)
        onRowClick?.(insightData!)
      }}
    />
  )
}

export default Table
